import { Stack, Typography } from '@mui/material'

import { Maybe, UserBankInput } from '@/clients/utoniq-core/schema'
import { UserBank } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { ResaleItem } from '@/components/organisms'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

import { BankCreateForm } from './BankCreateForm'

export * from './fragments'

export type State = {
  bank: Maybe<
    Pick<
      UserBank,
      | 'bankName'
      | 'branchName'
      | 'branchCode'
      | 'accountType'
      | 'accountNumber'
      | 'accountHolderName'
    >
  >
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSubmit: (inputs: UserBankInput) => Promise<any>
}

export const BankCreateTemplate: Template<State, Action> = ({
  state,
  onSubmit,
}) => {
  const { t } = useTranslation('bankCreate')
  const { routerNames, routerPages } = useRouter()
  return (
    <>
      <Head title={routerNames.BankCreate} />
      <BaseLayout
        margin
        header={{
          back: true,
          defaultBackPage: routerPages.Setting,
          title: t('title'),
        }}
      >
        <CurrentBankCell bank={state.bank} />
        <BankCreateForm bank={state.bank} onSubmit={onSubmit} />
        <BankNoteCell />
      </BaseLayout>
    </>
  )
}

type CurrentBankCellProps = {
  bank: State['bank']
}
const CurrentBankCell: Cell<CurrentBankCellProps> = ({ bank }) => {
  const { t } = useTranslation('bankCreate')
  return (
    <CellFrame sx={{ pt: 2, pb: 8 }}>
      <Stack spacing={1}>
        <Typography variant="subtitle1" color="text.secondary">
          {t('registeredTransferAccount')}
        </Typography>
        {!bank ? (
          <Typography variant="body1" color="text.secondary">
            {t('registeredTransferAccountEmpty')}
          </Typography>
        ) : (
          <Typography variant="body1">
            {t('registeredTransferAccountValue', {
              ...bank,
              accountType: t(bank.accountType),
            })}
          </Typography>
        )}
      </Stack>
    </CellFrame>
  )
}

const BankNoteCell: Cell = () => {
  const { t } = useTranslation('bankCreate')
  return (
    <CellFrame>
      <ResaleItem title={t('bankNoteTitle')}>
        <Stack>
          <Typography variant="caption" color="text.secondary">
            {t('bankNoteDesc1')}
          </Typography>

          <a
            href="https://www.jp-bank.japanpost.jp/kojin/sokin/furikomi/kouza/kj_sk_fm_kz_1.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none' }}
          >
            <Typography color="primary.main" variant="caption">
              {t('bankNoteDescLink')}
            </Typography>
          </a>
          <Typography variant="caption" color="text.secondary">
            {t('bankNoteDesc2')}
          </Typography>
        </Stack>
      </ResaleItem>
    </CellFrame>
  )
}
